import React, { useEffect, useMemo, useRef } from "react";

import { Editor } from "@hugerte/hugerte-react";
import { useTranslation } from "react-i18next";

import { useTenant } from "multitenancy/config";
import { ProductId } from "multitenancy/product";

import {
  ICheckboxList,
  ICreateEditCustomerTemplate,
} from "pages/app/CustomerTemplate/interface";
import {
  ContainerInputs,
  ContainerSwitch,
} from "pages/app/StyleCSS/bodyCreateEdit";

import CheckBoxLabel from "components/CheckBoxLabel";
import { DropdownGlobal } from "components/DropdownGlobal";
import GeneralInput from "components/GeneralInput";
import Switch from "components/Switch";

import {
  KeyWordsTitle,
  BottomContainer,
  ContainerCheckboxList,
  ContainerTextEditor,
  ContainerBody,
  ContentInputDescription,
  ContentInputTitle,
} from "./styles";
import { editorInit } from "./tiny";

export const CreateEditCustomerTemplate = ({
  fieldError,
  setFieldError,
  title,
  description,
  template,
  type,
  actived,
  setTitle,
  keywords,
  setKeywords,
  setType,
  setTemplate,
  setDescription,
  setActived,
}: ICreateEditCustomerTemplate): JSX.Element => {
  const { t } = useTranslation();
  const { productId } = useTenant();
  const refTiny = useRef<any>(null);
  const isInqueritoProduct = productId === ProductId.Inquerito;

  const translatedDocumentTypeList = useMemo(
    () => [
      { id: 1, name: t("templates.documentTypes.ata") },
      { id: 2, name: t("templates.documentTypes.transcription") },
    ],
    [t],
  );

  const translatedCheckboxList = useMemo(() => {
    const hearingKeywords: Array<ICheckboxList> = [
      {
        id: 3,
        label: t("templates.keywordLabels.hearingNumber"),
        value: "ProcedureNumber",
      },
      { id: 4, label: t("templates.keywordLabels.county"), value: "Place" },
      {
        id: 5,
        label: t("templates.keywordLabels.openingDate"),
        value: "ExpectedDate",
      },
      {
        id: 6,
        label: t("templates.keywordLabels.duration"),
        value: "Duration",
      },
      {
        id: 8,
        label: t("templates.keywordLabels.deadline"),
        value: "ProcedureFinishDate",
      },
      {
        id: 10,
        label: t("templates.keywordLabels.hearingType"),
        value: "HearingTypeName",
      },
      {
        id: 11,
        label: t("templates.keywordLabels.transcription"),
        value: "Transcription",
      },
    ];

    const inquiryKeywords: Array<ICheckboxList> = [
      {
        id: 1,
        label: t("templates.keywordLabels.procedureSource"),
        value: "HearingSource",
      },
      {
        id: 2,
        label: t("templates.keywordLabels.procedureType"),
        value: "HearingTypeName",
      },
      {
        id: 3,
        label: t("templates.keywordLabels.procedureNumber"),
        value: "ProcedureNumber",
      },
      {
        id: 4,
        label: t("templates.keywordLabels.policeUnit"),
        value: "Place",
      },
      {
        id: 5,
        label: t("templates.keywordLabels.openingDate"),
        value: "ExpectedDate",
      },
      {
        id: 6,
        label: t("templates.keywordLabels.duration"),
        value: "Duration",
      },
      {
        id: 7,
        label: t("templates.keywordLabels.authority"),
        value: "AuthorityName",
      },
      {
        id: 8,
        label: t("templates.keywordLabels.deadline"),
        value: "ProcedureFinishDate",
      },
      {
        id: 9,
        label: t("templates.keywordLabels.attendant"),
        value: "AttendantName",
      },
      {
        id: 10,
        label: t("templates.keywordLabels.hearingFileType"),
        value: "HearingFileType",
      },
      {
        id: 11,
        label: t("templates.keywordLabels.transcription"),
        value: "Transcription",
      },
    ];

    return isInqueritoProduct ? inquiryKeywords : hearingKeywords;
  }, [isInqueritoProduct, t]);

  const onSelectKeyword = (keyword: string) => {
    const isSelected = keywords.includes(keyword);
    const isTranscriptionBlocked =
      type === 1 && keywords.includes("Transcription");
    const placeholder = `&lt;&lt;${keyword}&gt;&gt;`;
    const wrappedKeyword = `<strong>${placeholder}</strong>&nbsp`;

    if (isSelected || isTranscriptionBlocked) {
      setTemplate(template.replaceAll(placeholder, ""));
      setKeywords(keywords.filter((k) => k !== keyword));
      return;
    }

    const cleanTemplate = template.trim();
    const hasParagraph =
      cleanTemplate.startsWith("<p>") && cleanTemplate.endsWith("</p>");
    const newContent = hasParagraph
      ? cleanTemplate.replace("</p>", `${wrappedKeyword}</p>`)
      : `${cleanTemplate}${wrappedKeyword}`;

    setTemplate(newContent);
    setKeywords([...keywords, keyword]);
  };

  useEffect(() => {
    if (type === 1 && keywords.includes("Transcription")) {
      setTemplate(
        template.replace(
          `<strong>&lt;&lt;Transcription&gt;&gt;</strong>&nbsp;`,
          "",
        ),
      );
      setKeywords(keywords.filter((item) => item !== "Transcription"));
    }
  }, [type]);

  const onChangeTemplate = (e: string) => {
    const newKeywords: string[] = [];
    for (const checkbox of translatedCheckboxList) {
      if (e.includes(`&lt;&lt;${checkbox.value}&gt;&gt;`)) {
        newKeywords.push(checkbox.value);
      }
    }
    setKeywords(Array.from(new Set(newKeywords)));
    setTemplate(e);
  };

  const newCheckList =
    type === 1
      ? translatedCheckboxList.filter((x: ICheckboxList) => x.id !== 11)
      : translatedCheckboxList;

  return (
    <div>
      <ContainerBody>
        <ContainerSwitch>
          <Switch
            dataTest="switch_active"
            label={t("common.active")}
            checked={actived}
            onChange={() => setActived(!actived)}
          />
        </ContainerSwitch>

        <ContentInputDescription>
          <GeneralInput
            id="description"
            name="description"
            label={t("common.description")}
            value={description}
            onChange={(value) => {
              setDescription(value);
              setFieldError({ ...fieldError, description: false });
            }}
            error={fieldError.description}
            required
          />
        </ContentInputDescription>

        <ContainerInputs>
          <DropdownGlobal
            labelName={t("templates.documentType")}
            defaultValue={
              type
                ? translatedDocumentTypeList.find((dt) => dt.id === type)?.name
                : ""
            }
            setSelected={(e: any) => {
              setFieldError({ ...fieldError, type: false });
              setType(e);
            }}
            required
            showDefaultValue={!!type}
            marginRight="10px"
            width="195px"
            options={translatedDocumentTypeList}
            error={fieldError.type}
          />

          <ContentInputTitle>
            <GeneralInput
              id="title"
              name="title"
              label={t("labels.title")}
              value={title}
              onChange={(value) => {
                setTitle(value);
                setFieldError({ ...fieldError, title: false });
              }}
              error={fieldError.title}
              required
              height="50px"
            />
          </ContentInputTitle>
        </ContainerInputs>

        <ContainerTextEditor error={fieldError.template}>
          <Editor
            ref={refTiny}
            inline={false}
            onEditorChange={(e) => {
              setFieldError({ ...fieldError, template: false });
              onChangeTemplate(e);
            }}
            value={template}
            onInit={(editor) => (refTiny.current = editor)}
            init={{ ...editorInit, newline_behavior: "linebreak" }}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === "c") {
                event.preventDefault();
                alert(t("templates.cannotCopyText"));
              }
            }}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
          />
        </ContainerTextEditor>

        <BottomContainer>
          <KeyWordsTitle>{t("templates.keywords")}</KeyWordsTitle>
          <ContainerCheckboxList>
            {newCheckList.map((item) => (
              <CheckBoxLabel
                key={item.id}
                id={`keyword-${item.id}`}
                label={item.label}
                checked={keywords.includes(item.value)}
                onChange={() => onSelectKeyword(item.value)}
              />
            ))}
          </ContainerCheckboxList>
        </BottomContainer>
      </ContainerBody>
    </div>
  );
};
